import { useCallback, useEffect, useMemo, useState } from 'react';

// Renders a long list a chunk at a time. The caller still owns the full
// (filtered, sorted) array; this only decides how much of it is mounted right
// now, and grows the window when the sentinel scrolls into view. Same
// IntersectionObserver idiom as FileThumb, applied to rows instead of images.
//
// Usage:
//   const { visible, hasMore, loadMore, sentinelRef, visibleCount, total } =
//       useLazyList(rows, { pageSize: 25, resetKey: searchTerm });
//   ...{visible.map(renderRow)}
//   <LazyListFooter sentinelRef={sentinelRef} ... />
const DEFAULT_PAGE_SIZE = 25;

const useLazyList = (items, { pageSize = DEFAULT_PAGE_SIZE, resetKey = '' } = {}) => {
    const list = useMemo(() => items || [], [items]);
    const total = list.length;
    const [count, setCount] = useState(pageSize);

    // Callback ref rather than useRef: the sentinel can appear long after the
    // hook first runs (the tracking page's archived section is hidden until you
    // ask for it), and a plain ref would leave the observer unattached.
    const [sentinel, setSentinel] = useState(null);
    const sentinelRef = useCallback((node) => setSentinel(node), []);

    // A new search, sort or refetch starts the window over: otherwise a term
    // typed after scrolling 400 rows deep would mount 400 rows of results.
    useEffect(() => {
        setCount(pageSize);
    }, [list, pageSize, resetKey]);

    const visibleCount = Math.min(count, total);
    const hasMore = visibleCount < total;

    const visible = useMemo(() => list.slice(0, visibleCount), [list, visibleCount]);

    const loadMore = useCallback(() => {
        setCount((current) => current + pageSize);
    }, [pageSize]);

    useEffect(() => {
        if (!hasMore || !sentinel) return undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) loadMore();
            },
            { rootMargin: '400px' }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, loadMore, visibleCount, sentinel]);

    return { visible, visibleCount, total, hasMore, loadMore, sentinelRef };
};

export default useLazyList;
